/**
 * Email Dialog Component
 * Modal for sending ROI reports via email
 */

import React, { useState, useEffect } from 'react';
import { X, Mail, Send, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { EmailFormData, sendROIReportEmail } from '../../../services/emailService';
import { updateSessionEmailMetadata } from '../services/firestoreService';

interface EmailDialogProps {
  isOpen: boolean;
  onClose: () => void;
  sessionId: string;
  defaultRecipient?: string;
  defaultRecipientName?: string;
  organizationName?: string;
  senderName?: string;
  userEmail?: string;  // Email of the user who created the session
  sessionSaved?: boolean;  // Whether session has been saved to Firestore
  reportHTMLContent?: string | null;  // HTML content from the report tab
  reportUrl?: string;  // Direct URL to the report
  formData?: any;  // Complete form data
  results?: any;  // Complete calculation results
  enhancedData?: any;  // Enhanced savings and ROI data
}

export const EmailDialog: React.FC<EmailDialogProps> = ({
  isOpen,
  onClose,
  sessionId,
  defaultRecipient = '',
  defaultRecipientName = '',
  organizationName = '',
  senderName = '',
  userEmail = '',
  reportHTMLContent = null,
  reportUrl = '',
  formData,
  results,
  enhancedData
}) => {
  const [emailFormData, setEmailFormData] = useState<EmailFormData>({
    recipientEmail: defaultRecipient,
    recipientName: defaultRecipientName,
    ccEmail: '',
    personalMessage: '',
    senderName: senderName,
    sessionId: sessionId,
    userEmail: userEmail,
    reportUrl: reportUrl,  // Pass the report URL
    reportHTMLContent: null,  // Simplified - no longer sending full HTML
    formData: formData,  // Pass complete form data
    results: results,  // Pass complete results
    enhancedData: enhancedData  // Pass enhanced data
  } as any);
  
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [statusMessage, setStatusMessage] = useState('');
  
  // Update form data when dialog opens with latest props
  useEffect(() => {
    if (isOpen) {
      setEmailFormData({
        recipientEmail: defaultRecipient,
        recipientName: defaultRecipientName,
        ccEmail: '',
        personalMessage: '',
        senderName: senderName,
        sessionId: sessionId,
        userEmail: userEmail,
        reportUrl: reportUrl,
        reportHTMLContent: null,
        formData: formData,
        results: results,
        enhancedData: enhancedData
      } as any);
      // Reset status when opening
      setStatus('idle');
      setStatusMessage('');
    }
  }, [isOpen, defaultRecipient, defaultRecipientName, senderName, sessionId, userEmail, reportUrl, formData, results, enhancedData]);
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailFormData.recipientEmail)) {
      setStatus('error');
      setStatusMessage('Please enter a valid email address');
      return;
    }
    
    if (emailFormData.ccEmail && !emailRegex.test(emailFormData.ccEmail)) {
      setStatus('error');
      setStatusMessage('Please enter a valid CC email address');
      return;
    }
    
    setSending(true);
    setStatus('idle');
    setStatusMessage('Sending email...');
    
    try {
      const result = await sendROIReportEmail(emailFormData);
      
      if (result.success) {
        setStatus('success');
        setStatusMessage('Report sent successfully! Check your email.');
        
        // Update session email metadata for admin console
        try {
          const recipients = [emailFormData.recipientEmail];
          if (emailFormData.ccEmail?.trim()) {
            recipients.push(emailFormData.ccEmail.trim());
          }
          
          if (userEmail && sessionId) {
            await updateSessionEmailMetadata(userEmail, sessionId, recipients);
          }
        } catch (metadataError) {
          console.error('Error updating email metadata:', metadataError);
          // Don't fail the whole operation if metadata update fails
        }
        
        // Close dialog after 2 seconds on success
        setTimeout(() => {
          onClose();
          // Reset form
          setEmailFormData({
            ...emailFormData,
            personalMessage: ''
          });
          setStatus('idle');
          setStatusMessage('');
        }, 2000);
      } else {
        setStatus('error');
        setStatusMessage(result.error || 'Failed to send email. Please try again.');
      }
    } catch (error) {
      setStatus('error');
      setStatusMessage('An unexpected error occurred. Please try again.');
    } finally {
      setSending(false);
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-0 sm:p-4">
      <div className="bg-white sm:rounded-lg shadow-xl max-w-lg w-full h-full sm:h-auto sm:max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b">
          <div className="flex items-center gap-2">
            <Mail className="w-6 h-6 text-red-600" />
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900">
              Email Report
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 transition-colors"
            disabled={sending}
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        
        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-4">
          {/* Status Messages */}
          {status === 'success' && (
            <div className="flex items-center gap-2 p-4 bg-green-50 border border-green-200 rounded-lg">
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
              <p className="text-green-800">{statusMessage}</p>
            </div>
          )}
          
          {status === 'error' && (
            <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-lg">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
              <p className="text-red-800">{statusMessage}</p>
            </div>
          )}
          
          {/* To Email */}
          <div>
            <label htmlFor="recipientEmail" className="block text-sm font-medium text-gray-700 mb-1">
              To <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              id="recipientEmail"
              value={emailFormData.recipientEmail}
              onChange={(e) => setEmailFormData({ ...emailFormData, recipientEmail: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 text-sm sm:text-base"
              placeholder="recipient@example.com"
              required
              disabled={sending}
            />
          </div>
          
          {/* Recipient Name */}
          <div>
            <label htmlFor="recipientName" className="block text-sm font-medium text-gray-700 mb-1">
              Recipient Name
            </label>
            <input
              type="text"
              id="recipientName"
              value={emailFormData.recipientName || ''}
              onChange={(e) => setEmailFormData({ ...emailFormData, recipientName: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 text-sm sm:text-base"
              placeholder="John Doe"
              disabled={sending}
            />
          </div>
          
          {/* CC Email */}
          <div>
            <label htmlFor="ccEmail" className="block text-sm font-medium text-gray-700 mb-1">
              CC (Optional)
            </label>
            <input
              type="email"
              id="ccEmail"
              value={emailFormData.ccEmail || ''}
              onChange={(e) => setEmailFormData({ ...emailFormData, ccEmail: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 text-sm sm:text-base"
              placeholder="cc@example.com"
              disabled={sending}
            />
          </div>
          
          {/* Personal Message */}
          <div>
            <label htmlFor="personalMessage" className="block text-sm font-medium text-gray-700 mb-1">
              Personal Message (Optional)
            </label>
            <textarea
              id="personalMessage"
              value={emailFormData.personalMessage || ''}
              onChange={(e) => setEmailFormData({ ...emailFormData, personalMessage: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 text-sm sm:text-base"
              rows={4}
              placeholder="Add a personal note to accompany the report..."
              disabled={sending}
            />
            <p className="text-xs text-gray-500 mt-1">
              This message will appear in the email body along with the report summary.
            </p>
          </div>
          
          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              disabled={sending}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={sending || status === 'success'}
            >
              {sending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Send Report
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};